/**
 * Ending a season — the arithmetic behind turn `5d`'s confirmation dialog.
 *
 * ENDING A SEASON IS A REVIEW, NOT A BUTTON. Six things happen at once and the ones that are hard
 * to undo — recomputed keeper costs, a rebuilt board — are exactly the ones nobody notices went
 * wrong until August. So the dialog lists all six WITH THE COUNTS IT FOUND, which is the part
 * that makes it checkable: "38 players eligible · 4 aged out" is a claim a commissioner can
 * disagree with before it is written, and "recompute keeper costs" on its own is not.
 *
 * IT ALSO NAMES WHAT IS WRONG BEFORE IT STARTS. A payout table with no winner and a roster
 * missing a week both survive the rollover perfectly happily and become permanent history. The
 * blockers are advisory rather than fatal — a commissioner may know the missing week is a bye and
 * end the season anyway — but they are stated, because the alternative is discovering them a year
 * later in a screen nobody can edit.
 *
 * Nothing here writes anything. It computes the review; the screen does the writing, after a
 * typed confirmation.
 */

import { escalationChain } from './grid.js';
import { FAAB } from './faab.js';
import type {
  DraftState, FaabEntry, LeagueSettings, RuleProposal,
} from '../store/types.js';

/**
 * A count this review has not been given.
 *
 * `null` rather than `0`, and the difference is the whole point of the dialog. "0 players
 * eligible" is a claim; "not counted yet" is the truth while the rollover is not wired to the
 * roster archive. A confirmation screen whose figures are made up is worse than one with gaps in
 * it, because the gaps are the only thing that stops somebody trusting the rest.
 */
export type Count = number | null;

export interface RolloverInput {
  season: number;
  /** Final standings, champion first. Empty until the season is logged. */
  finishOrder: readonly string[];
  /** Every roster spot held at season end, for the archive count. Null when not yet counted. */
  rosterCount: Count;
  teams: number;
  /** Players whose current round value can still be kept next year. Null when not yet counted. */
  eligible: readonly { player: string; round: number; yearsKept: number }[] | null;
  /** Picks already traded away for next season, and any swap rights. */
  tradedPicks: Count;
  swapRights: Count;
  draftRounds: number;
  /** Provisional rules that must go back to the committee. */
  provisional: readonly { id: string; title: string }[];
  /** FAAB unspent per GM at season end. Null when the ledger has not been read. */
  unspent: Readonly<Record<string, number>> | null;
  faabBase: number;
  faabRolloverRate: number;
  /** Anything the season is missing. Advisory — see the module note. */
  problems: readonly string[];
}

export interface RolloverStep {
  id: string;
  label: string;
  /** The counts it found, which is what makes the step checkable rather than a promise. */
  detail: string;
  cites?: string[];
}

export interface RolloverReview {
  steps: RolloverStep[];
  problems: readonly string[];
  /** What a commissioner must type. Deliberately the season, so it cannot be muscle memory. */
  confirmWord: string;
}

/**
 * THERE IS NO UNDO BUTTON, and saying there was one would be the worst thing on this screen.
 *
 * An earlier draft of this promised the rollover was "reversible for 7 days", which nothing in the
 * app implemented — no journal, no dated snapshot, nothing to reverse it with. The real safety net
 * is the backup file: the screen downloads one before it writes anything, and restoring that file
 * from Saved data is the only thing that puts the league back. So the screen says that instead.
 */
export const UNDO_IS: string =
  'the backup file this downloads first — restoring it from Saved data is what puts the league '
  + 'back, and nothing else does';

export function rolloverReview(input: RolloverInput): RolloverReview {
  const next = input.season + 1;

  /*
   * Aged out, computed rather than asserted. A keeper is finished when the escalation chain has
   * no round left for him, which is the same walk the Grid page draws — so this count and that
   * page cannot disagree.
   */
  const agedOut = input.eligible === null ? null : input.eligible.filter((p) => {
    const chain = escalationChain(p.round);
    return chain.length <= p.yearsKept;
  }).length;

  const rolled = input.unspent === null ? null : Object.values(input.unspent)
    .reduce((n, v) => n + Math.floor(Math.max(0, v) * input.faabRolloverRate), 0);

  /** A figure, or an honest admission that there is not one yet. */
  const n = (v: Count, unit: string) => (v === null ? `${unit} not counted yet` : `${v} ${unit}`);

  return {
    steps: [
      {
        id: 'history',
        label: `Log ${input.season} to History`,
        detail: input.finishOrder.length === input.teams
          ? 'champion, runner-up, Toilet Bowl, top scorer, payouts'
          : `only ${input.finishOrder.length} of ${input.teams} places are recorded`,
      },
      {
        id: 'rosters',
        label: `Archive ${input.teams} rosters`,
        detail: input.rosterCount === null
          ? 'roster count not available yet'
          : `${input.rosterCount} players, with acquisition and cost`,
      },
      {
        id: 'keepers',
        label: `Recompute keeper costs for ${next}`,
        detail: input.eligible === null
          ? 'eligibility not computed yet'
          : `${input.eligible.length} players eligible · ${agedOut} aged out of the escalation`,
        cites: ['2019-II-1'],
      },
      {
        id: 'board',
        label: `Build the ${next} board`,
        detail: `${input.draftRounds} rounds · `
          + `${n(input.tradedPicks, 'traded picks')}`
          + `${input.swapRights ? ` and ${input.swapRights} swap right${input.swapRights === 1 ? '' : 's'}` : ''}`
          + ' carried across',
      },
      {
        id: 'proposals',
        label: 'Open rule proposals',
        detail: input.provisional.length === 0
          ? 'nothing queued for a mandatory re-vote'
          : `${input.provisional.length} provisional rule`
            + `${input.provisional.length === 1 ? '' : 's'} queued for mandatory re-vote`,
        cites: ['2019-I-2'],
      },
      {
        id: 'faab',
        label: 'Roll FAAB to opening balances',
        detail: rolled === null
          ? `₣${input.faabBase} base plus half of unspent — the ledger has not been read yet`
          : `₣${input.faabBase} base plus half of unspent — ₣${rolled} carried in total`,
        cites: ['2018-II-4'],
      },
    ],
    problems: input.problems,
    confirmWord: `end ${input.season}`,
  };
}

/* -- what it actually writes ------------------------------------------------------------------ */

/**
 * The rollover as a LIST OF WRITES, computed before any of them happen.
 *
 * Separated from the doing for the same reason the review is separated from the button: a
 * commissioner should be able to see the exact rows before they exist, and a test should be able
 * to check them without a database. `applyRollover` then does nothing but perform this list.
 *
 * WHAT IS AND IS NOT A WRITE, honestly. Three of the six review steps are records rather than
 * writes — the season's history, the roster archive and the recomputed keeper costs all DERIVE
 * from data the app already holds, which is the whole point of never persisting a computed cost.
 * Rolling the season does not need to write them and must not: a stored 2027 keeper cost would be
 * the stale-number failure this app was built to get away from.
 *
 * So the writes are the three things that genuinely have to exist as rows next year: the opening
 * FAAB balances, the provisional rules that owe a re-vote, and the settings and draft state that
 * stand the season up.
 */
export type RolloverWrite =
  | { kind: 'faab'; says: string; payload: FaabEntry }
  | { kind: 'proposal'; says: string; payload: RuleProposal }
  | { kind: 'settings'; says: string; payload: LeagueSettings }
  | { kind: 'draftState'; says: string; payload: DraftState };

export interface PlanInput {
  season: number;
  /** Unspent FAAB per GM at season end. */
  unspent: Readonly<Record<string, number>>;
  faabBase: number;
  faabRolloverRate: number;
  /** Provisional rules owing a mandatory re-vote next year. */
  provisional: readonly { id: string; title: string; body: string; amends: string[]; gm: string }[];
  /** Final finish, champion first — next year's lottery weights come off it. */
  finishOrder: readonly string[];
  by: string;
  now: string;
}

export function rolloverPlan(input: PlanInput): RolloverWrite[] {
  const next = input.season + 1;
  const out: RolloverWrite[] = [];

  /*
   * FAAB, as an OPENING ENTRY per GM rather than a balance.
   *
   * The ledger is append-only and a balance is derived from it (see `domain/faab.ts`); writing a
   * balance would put a number in storage that the ledger then disagrees with the moment anybody
   * adds a row. So the rollover writes the same kind of row every other FAAB movement is.
   *
   * SORTED BY GM, because `Object.entries` is insertion-ordered and the list a commissioner reads
   * before confirming should not change its running order depending on who spent first.
   */
  for (const gm of Object.keys(input.unspent).sort()) {
    const unspent = input.unspent[gm] ?? 0;
    const carried = Math.floor(Math.max(0, unspent) * input.faabRolloverRate);
    out.push({
      kind: 'faab',
      says: `${gm}: ${FAAB}${input.faabBase} base plus ${FAAB}${carried} carried `
        + `from ${FAAB}${unspent} unspent`,
      payload: {
        id: `rollover-${next}-${gm.toLowerCase()}`,
        season: next,
        gm,
        amount: input.faabBase + carried,
        kind: 'allotment',
        description: `${next} opening allotment — ${FAAB}${input.faabBase} base plus `
          + `${FAAB}${carried} rolled over from ${FAAB}${unspent} unspent in ${input.season} `
          + '(2018-II-4)',
        date: input.now,
        recordedBy: input.by,
      },
    });
  }

  /*
   * A provisional rule owes a mandatory re-vote, and it is written as a NEW PROPOSAL rather than
   * by flipping a flag on the old one. The 2026 record of what the committee decided in 2026 must
   * not change because 2027 happened — the same "a season is a version" rule the config follows.
   *
   * It lands `docketed`, not `submitted`: 2015-I-1 puts it on the agenda automatically, so making
   * somebody re-submit it would be the app inventing a step the rule does not have.
   */
  for (const p of input.provisional) {
    out.push({
      kind: 'proposal',
      says: `${p.title} — on the ${next} docket for its mandatory re-vote`,
      payload: {
        id: `revote-${next}-${p.id}`,
        season: next,
        gm: p.gm,
        title: p.title,
        body: p.body,
        amends: p.amends,
        status: 'docketed',
        isRevote: true,
        createdAt: input.now,
        updatedAt: input.now,
      },
    });
  }

  /*
   * Next season's settings, with EVERY DATE NULL.
   *
   * Carrying this year's dates forward would put five plausible wrong deadlines on the calendar,
   * and a wrong deadline is worse than a missing one — the app would count down to it and
   * managers would work to it. The committee sets them; until it does, the screens say so.
   */
  out.push({
    kind: 'settings',
    says: `${next} opens with no dates set — the committee sets them`,
    payload: {
      season: next,
      keeperDeadline: null,
      declarationsLocked: false,
      preferenceDeadline: null,
      preferencesLocked: false,
      proposalDeadline: null,
      committeeDate: null,
      draftDate: null,
      updatedAt: input.now,
      updatedBy: input.by,
    },
  });

  /*
   * Next season's board: the finish order carried, the ORDER ITSELF EMPTY, unpublished.
   *
   * The finish is a fact the season produced and the lottery needs. The slot order is not — it
   * comes out of the 2020-II-1 process, which has not been run. Writing a guess at it and marking
   * it unpublished would still show ten men a draft order somebody would screenshot.
   */
  out.push({
    kind: 'draftState',
    says: `${next} board carries ${input.finishOrder.length} finishing place`
      + `${input.finishOrder.length === 1 ? '' : 's'}, order not yet drawn`,
    payload: {
      season: next,
      order: [],
      status: 'setup',
      onTheClock: 1,
      finishOrder: [...input.finishOrder],
      published: false,
      updatedAt: input.now,
    },
  });

  return out;
}

/**
 * Perform the plan. Nothing but the loop — every decision was made in `rolloverPlan`.
 *
 * IN PLAN ORDER, one at a time, and a failure stops the run rather than pressing on. A half
 * rollover that got through the FAAB rows and died on the docket is recoverable by looking at
 * what exists; one that skipped a row in the middle and finished cleanly is not, because nothing
 * says which row is missing.
 */
export async function applyRollover(plan: readonly RolloverWrite[], store: RolloverStore): Promise<void> {
  for (const w of plan) {
    if (w.kind === 'faab') await store.saveFaab(w.payload);
    else if (w.kind === 'proposal') await store.saveProposal(w.payload);
    else if (w.kind === 'settings') await store.saveSettings(w.payload);
    else await store.saveDraftState(w.payload);
  }
}

/** Just the four methods the rollover needs, so a test does not have to build a whole store. */
export interface RolloverStore {
  saveFaab(e: FaabEntry): Promise<void>;
  saveProposal(p: RuleProposal): Promise<void>;
  saveSettings(s: LeagueSettings): Promise<void>;
  saveDraftState(d: DraftState): Promise<void>;
}

/**
 * The three review steps that are records rather than writes, named so the screen can say so.
 *
 * Stating this is not pedantry. "Recompute keeper costs for 2027" sounds like a write and is not:
 * costs derive from the roster and the rules in force, so the recomputation happens every time
 * anybody opens the screen. A rollover that wrote them would freeze 2027's prices against 2026's
 * rulebook, which is precisely the spreadsheet failure this app replaced.
 */
export const DERIVED_STEPS: Readonly<Record<string, string>> = {
  history: 'Derived from the box scores and standings already in the build — nothing is written.',
  rosters: 'Derived from the pick ledger — nothing is written.',
  keepers: 'Costs derive from the roster and the rules in force, every time the screen opens. '
    + 'Writing them would freeze next year\u2019s prices against this year\u2019s rulebook.',
};
